import apiClient from './client';
import type {
  SyncEvent,
  PaginatedResponse,
  SessionSummary,
  SyncEventStats,
  DeviceSummary,
  UserSummary,
  SyncEventFilters,
} from '../types';

/**
 * SyncEvent API endpoints
 * 
 * These functions interact with the backend sync event API.
 * Endpoints to be implemented on backend as per SYNC_EVENT_FRONTEND_GUIDE.md
 */

/**
 * Build query params from filters object
 */
function buildParams(filters?: SyncEventFilters): Record<string, string | number | boolean> {
  if (!filters) return {};

  const params: Record<string, string | number | boolean> = {};

  if (filters.user_email) params.user_email = filters.user_email;
  if (filters.device_code) params.device_code = filters.device_code;
  if (filters.file_id) params.file_id = filters.file_id;
  if (filters.session_id) params.session_id = filters.session_id;
  if (filters.firmware_version) params.firmware_version = filters.firmware_version;
  if (filters.timestamp_gte) params.timestamp__gte = filters.timestamp_gte;
  if (filters.timestamp_lte) params.timestamp__lte = filters.timestamp_lte;
  if (filters.ordering) params.ordering = filters.ordering;
  if (filters.page) params.page = filters.page;
  if (filters.page_size) params.page_size = filters.page_size;

  if (filters.has_suspicious_chars !== undefined) {
    params.has_suspicious_chars = filters.has_suspicious_chars;
  }
  if (filters.was_quarantined !== undefined) {
    params.was_quarantined = filters.was_quarantined;
  }
  if (filters.quarantine_reason) {
    params.quarantine_reason = filters.quarantine_reason;
  }
  if (filters.is_first_sync !== undefined) {
    params.is_first_sync = filters.is_first_sync;
  }
  if (filters.success !== undefined) {
    params.success = filters.success;
  }

  // Deduplication filters
  if (filters.has_name_deduplication !== undefined) {
    params.has_name_deduplication = filters.has_name_deduplication;
  }
  if (filters.is_mass_deduplication !== undefined) {
    params.is_mass_deduplication = filters.is_mass_deduplication;
  }

  if (filters.event_type) {
    if (Array.isArray(filters.event_type)) {
      params.event_type__in = filters.event_type.join(',');
    } else {
      params.event_type = filters.event_type;
    }
  }

  return params;
}

// Base path for sync event admin endpoints
const SYNC_EVENTS_PATH = '/admin/sync-events';

/**
 * List sync events with optional filtering and pagination
 */
export async function getSyncEvents(
  filters?: SyncEventFilters
): Promise<PaginatedResponse<SyncEvent>> {
  const response = await apiClient.get<PaginatedResponse<SyncEvent>>(
    `${SYNC_EVENTS_PATH}/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get a single sync event by ID
 */
export async function getSyncEvent(id: string): Promise<SyncEvent> {
  const response = await apiClient.get<SyncEvent>(`${SYNC_EVENTS_PATH}/${id}/`);
  return response.data;
}

/**
 * List unique sessions with summary stats
 */
export async function getSessions(
  filters?: Pick<SyncEventFilters, 'user_email' | 'device_code' | 'timestamp_gte' | 'timestamp_lte' | 'page' | 'page_size'>
): Promise<PaginatedResponse<SessionSummary>> {
  const response = await apiClient.get<PaginatedResponse<SessionSummary>>(
    `${SYNC_EVENTS_PATH}/sessions/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get all events in a specific session
 */
export async function getSessionEvents(sessionId: string): Promise<SyncEvent[]> {
  const response = await apiClient.get<SyncEvent[]>(
    `${SYNC_EVENTS_PATH}/sessions/${sessionId}/`
  );
  return response.data;
}

/**
 * Get overall sync event stats
 */
export async function getStats(
  filters?: Pick<SyncEventFilters, 'timestamp_gte' | 'timestamp_lte'>
): Promise<SyncEventStats> {
  const response = await apiClient.get<SyncEventStats>(
    `${SYNC_EVENTS_PATH}/stats/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get events grouped by user
 */
export async function getEventsByUser(
  filters?: Pick<SyncEventFilters, 'timestamp_gte' | 'timestamp_lte' | 'page' | 'page_size'>
): Promise<PaginatedResponse<UserSummary>> {
  const response = await apiClient.get<PaginatedResponse<UserSummary>>(
    `${SYNC_EVENTS_PATH}/by-user/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get events grouped by device
 */
export async function getEventsByDevice(
  filters?: Pick<SyncEventFilters, 'user_email' | 'timestamp_gte' | 'timestamp_lte' | 'page' | 'page_size'>
): Promise<PaginatedResponse<DeviceSummary>> {
  const response = await apiClient.get<PaginatedResponse<DeviceSummary>>(
    `${SYNC_EVENTS_PATH}/by-device/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get only corrupted sync events
 */
export async function getCorruptedEvents(
  filters?: Omit<SyncEventFilters, 'has_suspicious_chars'>
): Promise<PaginatedResponse<SyncEvent>> {
  const response = await apiClient.get<PaginatedResponse<SyncEvent>>(
    `${SYNC_EVENTS_PATH}/corrupted/`,
    { params: buildParams(filters) }
  );
  return response.data;
}

/**
 * Get all sync events for a specific file
 */
export async function getFileHistory(fileId: string): Promise<SyncEvent[]> {
  const response = await apiClient.get<SyncEvent[]>(
    `${SYNC_EVENTS_PATH}/file-history/${fileId}/`
  );
  return response.data;
}

/**
 * Get sync events with name deduplication
 */
export async function getDeduplicationEvents(
  filters?: Omit<SyncEventFilters, 'has_name_deduplication'>
): Promise<PaginatedResponse<SyncEvent>> {
  const response = await apiClient.get<PaginatedResponse<SyncEvent>>(
    `${SYNC_EVENTS_PATH}/`,
    { params: { ...buildParams(filters), has_name_deduplication: true } }
  );
  return response.data;
}

/**
 * Get mass deduplication events (alert-worthy)
 */
export async function getMassDeduplicationEvents(
  filters?: Omit<SyncEventFilters, 'is_mass_deduplication'>
): Promise<PaginatedResponse<SyncEvent>> {
  const response = await apiClient.get<PaginatedResponse<SyncEvent>>(
    `${SYNC_EVENTS_PATH}/`,
    { params: { ...buildParams(filters), is_mass_deduplication: true } }
  );
  return response.data;
}

/**
 * Get conflict events (created or resolved)
 */
export async function getConflictEvents(
  filters?: Omit<SyncEventFilters, 'event_type'>,
  type: 'created' | 'resolved' | 'all' = 'all'
): Promise<PaginatedResponse<SyncEvent>> {
  const eventType = type === 'all' 
    ? 'conflict_created,conflict_resolved'
    : type === 'created' 
      ? 'conflict_created' 
      : 'conflict_resolved';
  
  const response = await apiClient.get<PaginatedResponse<SyncEvent>>(
    `${SYNC_EVENTS_PATH}/`,
    { params: { ...buildParams(filters), event_type__in: eventType } }
  );
  return response.data;
}

export default {
  getSyncEvents,
  getSyncEvent,
  getSessions,
  getSessionEvents,
  getStats,
  getEventsByUser,
  getEventsByDevice,
  getCorruptedEvents,
  getFileHistory,
  getDeduplicationEvents,
  getMassDeduplicationEvents,
  getConflictEvents,
};

